import React, { useEffect, useState } from "react";
import { gaTrackCta } from "../analytics/ga4";
import { C } from "../core/constants";
import { localizedArticlesPath, localizedPath } from "../core/localePaths";
import { useIsMobile } from "../hooks/useMediaQuery";
import Button from "./Button";
import styles from "./PublicTopNav.module.css";

export default function PublicTopNav({
  acc,
  t,
  navigateToPage,
  showBrandClick = true,
  analyticsScreen = "public",
  locale,
  setLocale,
  onLocaleNavigate = null,
  LanguageSwitcher,
}) {
  const screen = analyticsScreen;
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile && menuOpen) setMenuOpen(false);
  }, [isMobile, menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const activeLocale = locale || "en";

  const navLinks = [
    {
      id: "pricing",
      label: t("pricing"),
      onClick: () => {
        const destination = localizedPath("pricing", activeLocale);
        gaTrackCta({ cta_id: "public_nav_pricing", cta_label: t("pricing"), destination, screen_class: screen });
        window.location.assign(destination);
        closeMenu();
      },
    },
    {
      id: "about",
      label: t("about"),
      onClick: () => {
        gaTrackCta({ cta_id: "public_nav_about", cta_label: t("about"), destination: "about", screen_class: screen });
        navigateToPage("about");
        closeMenu();
      },
    },
    {
      id: "articles",
      label: t("articles_insights"),
      onClick: () => {
        const destination = localizedArticlesPath(activeLocale);
        gaTrackCta({
          cta_id: "public_nav_articles",
          cta_label: t("articles_insights"),
          destination,
          screen_class: screen,
        });
        window.location.assign(destination);
        closeMenu();
      },
    },
    {
      id: "contact",
      label: t("contact_us"),
      onClick: () => {
        gaTrackCta({
          cta_id: "public_nav_contact",
          cta_label: t("contact_us"),
          destination: localizedPath("contact", activeLocale),
          screen_class: screen,
        });
        window.location.assign(localizedPath("contact", activeLocale));
        closeMenu();
      },
    },
  ];

  return (
    <>
      {menuOpen && (
        <button type="button" className={styles.mobileBackdropOpen} aria-label={t("close_menu")} onClick={closeMenu} />
      )}
      <div className={styles.bar}>
        <div
          className={styles.brand}
          style={{ color: acc.primary }}
          onClick={
            showBrandClick
              ? () => {
                  gaTrackCta({ cta_id: "public_nav_brand", cta_label: "Ecliptica", destination: "landing", screen_class: screen });
                  navigateToPage("landing");
                }
              : undefined
          }
        >
          ✦ Ecliptica
        </div>
        <button
          type="button"
          className={styles.menuButton}
          aria-expanded={menuOpen}
          aria-label={t("nav_menu")}
          onClick={() => setMenuOpen((prev) => !prev)}
          style={{ color: acc.primary }}
        >
          {menuOpen ? "✕" : "☰"}
        </button>
        <div className={styles.actions}>
          {navLinks.map((link) => (
            <span key={link.id} className={styles.link} style={{ color: C.textSecondary }} onClick={link.onClick} role="presentation">
              {link.label}
            </span>
          ))}
          <Button
            variant="outline"
            acc={acc}
            onClick={() => {
              const destination = localizedPath("login", activeLocale);
              gaTrackCta({ cta_id: "public_nav_sign_in", cta_label: t("sign_in"), destination, screen_class: screen });
              window.location.assign(destination);
            }}
            style={{ padding: "8px 20px", fontSize: 14 }}
          >
            {t("sign_in")}
          </Button>
          <Button
            variant="primary"
            acc={acc}
            onClick={() => {
              const destination = localizedPath("signUp", activeLocale);
              gaTrackCta({ cta_id: "public_nav_sign_up", cta_label: t("get_started"), destination, screen_class: screen });
              window.location.assign(destination);
            }}
            style={{ padding: "8px 20px", fontSize: 14 }}
          >
            {t("get_started")}
          </Button>
          {!isMobile && LanguageSwitcher && locale != null && setLocale && (
            <LanguageSwitcher locale={locale} onChange={setLocale} acc={acc} onNavigate={onLocaleNavigate} />
          )}
        </div>
      </div>
      {menuOpen && isMobile && (
        <div className={styles.mobilePanelOpen} role="dialog" aria-modal="true" aria-label={t("nav_menu")}>
          {navLinks.map((link) => (
            <span key={link.id} className={styles.mobileLink} style={{ color: C.textSecondary }} onClick={link.onClick} role="presentation">
              {link.label}
            </span>
          ))}
          <div className={styles.mobileActions}>
            <Button
              variant="outline"
              acc={acc}
              onClick={() => {
                const destination = localizedPath("login", activeLocale);
                gaTrackCta({ cta_id: "public_nav_sign_in", cta_label: t("sign_in"), destination, screen_class: screen });
                window.location.assign(destination);
                closeMenu();
              }}
              style={{ padding: "12px 20px", fontSize: 14, width: "100%" }}
            >
              {t("sign_in")}
            </Button>
            <Button
              variant="primary"
              acc={acc}
              onClick={() => {
                const destination = localizedPath("signUp", activeLocale);
                gaTrackCta({ cta_id: "public_nav_sign_up", cta_label: t("get_started"), destination, screen_class: screen });
                window.location.assign(destination);
                closeMenu();
              }}
              style={{ padding: "12px 20px", fontSize: 14, width: "100%" }}
            >
              {t("get_started")}
            </Button>
          </div>
          {LanguageSwitcher && locale != null && setLocale && (
            <div className={styles.mobileLocale}>
              <LanguageSwitcher locale={locale} onChange={setLocale} acc={acc} menuAlign="start" onNavigate={onLocaleNavigate} />
            </div>
          )}
        </div>
      )}
    </>
  );
}
